import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  createDailyMission,
  deleteDailyMission,
  getDailyMission,
  getDailyMissionsPayload,
  listDailyMissionActions,
  listDailyMissions,
  updateDailyMission,
} from '../../services/dailyMissionsApi';
import {
  DEFAULT_MISSION_TITLE,
  MAX_TASKS,
  addDays,
  buildCreateRequest,
  buildUpdateRequest,
  copyMissionToForm,
  defaultTaskTitle,
  deleteResultMessage,
  emptyForm,
  emptyTask,
  formatMissionDate,
  isDateConflict,
  missionToForm,
  navigationSlotCount,
  parseActionOptions,
  parseConflicts,
  parseDayList,
  parseDeleteResult,
  parseMission,
  parseSavedMission,
  resizeNavigationLinks,
  validateForm,
} from './dailyMissionUtils';
import { useDailyMissionsSession } from './useDailyMissionsSession';

/** First date from `start` onwards that has no mission yet. */
function firstFreeDate(start, takenDates) {
  let day = start;
  for (let i = 0; i < 366 && takenDates.has(day); i += 1) day = addDays(day, 1);
  return day;
}

function linkHelper(action) {
  const destination = action?.default_link || '';
  const key = action?.action_key || '';
  if (destination.includes('fan_journey') || key.startsWith('fan_journey')) {
    return 'Leave empty to open the signed-in player’s fan journey profile. Type a link to lock that tap.';
  }
  return 'Leave empty and the app picks this tap. Type a link to lock only that tap.';
}

/** Titles the backend generated from the action label are shown as empty so they follow later count changes. */
function clearAutoTitles(mission, labels) {
  return {
    ...mission,
    tasks: (mission.tasks || []).map((task) =>
      task.title === defaultTaskTitle(labels.get(task.action_key), task.target_value) ? { ...task, title: '' } : task
    ),
  };
}

function DailyMissionEditor() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const copyFromId = searchParams.get('copy');
  const location = useLocation();
  const navigate = useNavigate();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(emptyForm());
  const [originalDate, setOriginalDate] = useState('');
  const [assignedCount, setAssignedCount] = useState(0);
  const [tasksLoaded, setTasksLoaded] = useState(isNew);
  const [actionOptions, setActionOptions] = useState([]);
  const [occupiedDates, setOccupiedDates] = useState([]);
  const [conflictOpen, setConflictOpen] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { handleApiResponse } = useDailyMissionsSession(setError);

  const loadData = useCallback(
    async ({ silent = false } = {}) => {
      if (!isNew && !/^\d+$/.test(String(id))) {
        navigate('/admin/daily-missions', { replace: true });
        return;
      }
      if (!silent) {
        setLoading(true);
        setError('');
      }
      try {
        const [actionsRes, listRes] = await Promise.all([listDailyMissionActions(), listDailyMissions()]);
        if (handleApiResponse(actionsRes, 'Failed to load actions.')) return;
        const actions = parseActionOptions(getDailyMissionsPayload(actionsRes));
        setActionOptions(actions);
        const labels = new Map(actions.map((action) => [action.action_key, action.label]));
        const catalogueLinks = new Map(actions.map((action) => [action.action_key, action.default_link]));
        const toForm = (mission) =>
          missionToForm(clearAutoTitles(mission, labels), { actionLinks: catalogueLinks });

        if (handleApiResponse(listRes, 'Failed to load daily missions.')) return;
        const { today, occupiedDates: scheduled } = parseDayList(getDailyMissionsPayload(listRes));
        const occupied = new Set(scheduled);
        setOccupiedDates(scheduled);

        if (isNew) {
          const date = firstFreeDate(today, occupied);
          if (copyFromId && /^\d+$/.test(copyFromId)) {
            const sourceRes = await getDailyMission(copyFromId);
            if (handleApiResponse(sourceRes, 'Failed to load the day to copy.')) return;
            const source = parseMission(getDailyMissionsPayload(sourceRes));
            setForm(source ? copyMissionToForm(clearAutoTitles(source, labels), date, { actionLinks: catalogueLinks }) : emptyForm(date));
          } else {
            setForm(emptyForm(date));
          }
          setOriginalDate('');
          setAssignedCount(0);
          setTasksLoaded(true);
          return;
        }

        const detailRes = await getDailyMission(id);
        if (handleApiResponse(detailRes, 'Failed to load this day.')) return;
        const mission = parseMission(getDailyMissionsPayload(detailRes));
        if (!mission) {
          setError('This day could not be loaded.');
          return;
        }
        setOriginalDate(mission.mission_date || '');
        setAssignedCount(Number(mission.assigned_count) || 0);
        setTasksLoaded(Array.isArray(mission.tasks));
        setForm(toForm(mission));
      } catch (fetchError) {
        setError(fetchError.message || 'Failed to load this day.');
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [copyFromId, handleApiResponse, id, isNew, navigate]
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const notice = location.state?.notice;
    if (!notice) return;
    setSnackbar({ open: true, message: notice, severity: 'success' });
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, navigate]);

  const actionKeys = useMemo(() => new Set(actionOptions.map((a) => a.action_key)), [actionOptions]);
  const actionByKey = useMemo(
    () => new Map(actionOptions.map((action) => [action.action_key, action])),
    [actionOptions]
  );
  const actionLabels = useMemo(
    () => new Map(actionOptions.map((action) => [action.action_key, action.label])),
    [actionOptions]
  );
  const actionLinks = useMemo(
    () => new Map(actionOptions.map((action) => [action.action_key, action.default_link])),
    [actionOptions]
  );
  const takenDates = useMemo(() => {
    const taken = new Set(occupiedDates);
    if (!isNew && originalDate) taken.delete(originalDate);
    return taken;
  }, [occupiedDates, isNew, originalDate]);
  const dateTaken = takenDates.has(form.mission_date);

  const updateField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const updateTask = (index, field, value) => {
    setForm((prev) => {
      const tasks = [...prev.tasks];
      const task = { ...tasks[index], [field]: value };
      if (field === 'action_key' && value !== tasks[index].action_key) {
        task.navigation_link = resizeNavigationLinks([], task.target_value);
        task.app_links = [];
        task.app_action_key = '';
      }
      tasks[index] = task;
      return { ...prev, tasks };
    });
  };

  const updateTapLink = (index, tap, value) => {
    setForm((prev) => {
      const tasks = [...prev.tasks];
      const task = tasks[index];
      const count = Math.max(navigationSlotCount(task.target_value), tap + 1, (task.navigation_link || []).length);
      const links = resizeNavigationLinks(task.navigation_link, count);
      links[tap] = value;
      tasks[index] = { ...task, navigation_link: links };
      return { ...prev, tasks };
    });
  };

  const addTask = () => setForm((prev) => ({ ...prev, tasks: [...prev.tasks, emptyTask()] }));

  const removeTask = (index) =>
    setForm((prev) => ({ ...prev, tasks: prev.tasks.filter((_, taskIndex) => taskIndex !== index) }));

  const openConflict = (found) => {
    setConflicts(found);
    setConflictOpen(true);
  };

  const handleSave = async (override = false) => {
    if (!tasksLoaded) {
      setError('Tasks did not load. Refresh this page before saving so the task list is not replaced with nothing.');
      return;
    }
    const validationError = validateForm(form, { actionKeys });
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!override && dateTaken) {
      openConflict([]);
      return;
    }

    setSaving(true);
    setError('');
    try {
      const requestOptions = { actionLabels, actionLinks, override };
      const result = isNew
        ? await createDailyMission(buildCreateRequest(form, requestOptions))
        : await updateDailyMission(id, buildUpdateRequest(form, requestOptions));
      if (isDateConflict(result)) {
        openConflict(parseConflicts(result));
        return;
      }
      if (handleApiResponse(result, 'Failed to save this day.')) return;

      const data = getDailyMissionsPayload(result);
      const saved = parseSavedMission(data);
      const movingDate = !isNew && originalDate && form.mission_date !== originalDate;
      const overrode = Number(data?.overridden_count) > 0 || override;
      const notice = overrode
        ? movingDate
          ? `This mission now uses ${formatMissionDate(form.mission_date)}. The previous mission on that date was released.`
          : `Updated the mission on ${formatMissionDate(form.mission_date)}. An unfinished day picks up these tasks. A finished day stays finished.`
        : isNew
          ? `Mission for ${formatMissionDate(form.mission_date)} created.`
          : assignedCount > 0
            ? 'Saved. An unfinished day picks up a new task. An action change on a task that is not done starts that count over. A finished day stays finished.'
            : 'Saved.';
      setConflictOpen(false);
      if (!isNew && saved) {
        setOriginalDate(saved.mission_date || '');
        setAssignedCount(Number(saved.assigned_count) || 0);
        setForm(missionToForm(clearAutoTitles(saved, actionLabels), { actionLinks }));
      }
      if (isNew) {
        navigate(saved?.id ? `/admin/daily-missions/${saved.id}` : '/admin/daily-missions', { state: { notice } });
        return;
      }
      setSnackbar({ open: true, message: notice, severity: 'success' });
      await loadData({ silent: true });
    } catch (saveError) {
      setError(saveError.message || 'Failed to save this day.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) return;
    setDeleting(true);
    setError('');
    try {
      const result = await deleteDailyMission(id);
      if (handleApiResponse(result, 'Failed to delete this day.')) return;
      setConfirmDelete(false);
      navigate('/admin/daily-missions', {
        state: { notice: deleteResultMessage(parseDeleteResult(getDailyMissionsPayload(result))) },
      });
    } catch (deleteError) {
      setError(deleteError.message || 'Failed to delete this day.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton component={Link} to="/admin/daily-missions" aria-label="Back to daily missions">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          {isNew ? 'Add daily mission' : `Daily mission · ${formatMissionDate(form.mission_date)}`}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {assignedCount > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {assignedCount} player{assignedCount === 1 ? ' has' : 's have'} already opened this day. A day they
          finished stays finished. An unfinished day picks up a task you add later. Changing the action on a task
          they have not finished starts that task’s count over. An empty In app box leaves that tap to the app. A
          link you type locks only that tap. Turning the day off hides it from players who have not opened it yet,
          and the date stays occupied.
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Date"
                type="date"
                fullWidth
                required
                value={form.mission_date}
                onChange={(e) => updateField('mission_date', e.target.value)}
                InputLabelProps={{ shrink: true }}
                helperText={
                  dateTaken
                    ? 'This date already has a mission. Saving will ask you to override it.'
                    : form.mission_date
                      ? formatMissionDate(form.mission_date)
                      : 'One mission per date, including inactive days'
                }
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Mission name"
                fullWidth
                value={form.title}
                placeholder={DEFAULT_MISSION_TITLE}
                onChange={(e) => updateField('title', e.target.value)}
                helperText={`Leave as “${DEFAULT_MISSION_TITLE}” unless you want a special name`}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Reward FE"
                type="number"
                fullWidth
                value={form.fan_energy_reward}
                onChange={(e) => updateField('fan_energy_reward', e.target.value)}
                inputProps={{ min: 0 }}
                helperText="Given when all tasks are done"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Subtitle (optional)"
                fullWidth
                value={form.subtitle}
                onChange={(e) => updateField('subtitle', e.target.value)}
                helperText="Leave empty if this day does not need a subtitle"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Image URL (optional)"
                fullWidth
                value={form.image_url}
                onChange={(e) => updateField('image_url', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch checked={!!form.is_active} onChange={(e) => updateField('is_active', e.target.checked)} />
                }
                label="Active. Turn off to hide this day from players who have not opened it yet."
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Tasks ({form.tasks.length}/{MAX_TASKS})
            </Typography>
            <Button
              startIcon={<AddIcon />}
              onClick={addTask}
              size="small"
              disabled={form.tasks.length >= MAX_TASKS}
            >
              Add task
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 70 }}>Task</TableCell>
                <TableCell>Action</TableCell>
                <TableCell sx={{ width: 110 }}>How many</TableCell>
                <TableCell>Title shown in app (optional)</TableCell>
                <TableCell>In app</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {form.tasks.map((task, index) => {
                const action = actionByKey.get(task.action_key);
                const tapCount = navigationSlotCount(task.target_value);
                const tapLinks = resizeNavigationLinks(task.navigation_link, tapCount);
                const showResolved = task.app_action_key === task.action_key;
                return (
                  <TableRow key={task.client_id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        Task {index + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        label="Action"
                        value={task.action_key}
                        onChange={(e) => updateTask(index, 'action_key', e.target.value)}
                        helperText={action?.description || ' '}
                      >
                        <MenuItem value="">Select action</MenuItem>
                        {actionOptions.map((option) => (
                          <MenuItem key={option.action_key} value={option.action_key}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        label="Times"
                        value={task.target_value}
                        onChange={(e) => updateTask(index, 'target_value', e.target.value)}
                        inputProps={{ min: 1 }}
                        helperText=" "
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Title"
                        value={task.title}
                        onChange={(e) => updateTask(index, 'title', e.target.value)}
                        placeholder={defaultTaskTitle(action?.label, task.target_value) || 'e.g. Rate any 3 players'}
                        InputLabelProps={{ shrink: true }}
                        helperText="Empty = action name"
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {tapLinks.map((link, tap) => {
                          const resolved = showResolved ? task.app_links?.[tap] : '';
                          return (
                            <TextField
                              key={`${task.client_id}-tap-${tap}`}
                              size="small"
                              fullWidth
                              label={tapCount > 1 ? `In app ${tap + 1}` : 'In app'}
                              value={link}
                              onChange={(e) => updateTapLink(index, tap, e.target.value)}
                              placeholder={resolved || action?.default_link || 'App chooses this tap'}
                              InputLabelProps={{ shrink: true }}
                              helperText={tap === tapCount - 1 ? linkHelper(action) : ' '}
                            />
                          );
                        })}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => removeTask(index)}
                        disabled={form.tasks.length <= 1}
                        aria-label={`Remove task ${index + 1}`}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box>
          {!isNew && (
            <Button color="error" onClick={() => setConfirmDelete(true)} disabled={saving || deleting}>
              Delete day
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button component={Link} to="/admin/daily-missions" disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => handleSave(false)} disabled={saving || deleting}>
            {saving ? 'Saving…' : isNew ? 'Create mission' : 'Save changes'}
          </Button>
        </Box>
      </Box>

      <Dialog open={conflictOpen} onClose={() => !saving && setConflictOpen(false)}>
        <DialogTitle>Override this date?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {conflicts.length
              ? conflicts
                  .map(
                    (row) =>
                      `${formatMissionDate(row.mission_date)}${row.existing_title ? ` (“${row.existing_title}”)` : ''}`
                  )
                  .join(', ')
              : formatMissionDate(form.mission_date)}{' '}
            already has a mission.{' '}
            {!isNew && originalDate && form.mission_date !== originalDate
              ? 'Overriding lets this mission take that day. The other mission is deleted if nobody has played it. If players already have it, its date is cleared and it is turned off, so their copy stays.'
              : 'Overriding updates that day instead of adding a second one. An unfinished day picks up a task you add later, and an unfinished task picks up an action change (that task’s count starts over). A day the player already finished stays finished.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflictOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => handleSave(true)} disabled={saving}>
            {saving ? 'Saving…' : 'Override'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmDelete} onClose={() => !deleting && setConfirmDelete(false)}>
        <DialogTitle>Delete this day?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            It is deleted if nobody has opened it. If players already have it, it is deactivated instead and their
            progress stays.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default DailyMissionEditor;
